import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { AuctionCard, type AuctionCardData } from "@/components/auction-card";
import { Reveal } from "@/components/reveal";
import { Skeleton } from "@/components/ui/skeleton";

const CARD_COLS =
  "id,title,images,end_time,current_bid_pence,starting_price_pence,bid_count,grading_company,grade,seller_id,buy_now_enabled,buy_now_price_pence,keep_buy_now_after_bid,reservation_expires_at,promoted_until,card_set";

/** Similar active auctions matched by card_set OR grading company, excluding current. */
export function SimilarAuctions({
  currentId,
  cardSet,
  gradingCompany,
}: {
  currentId: string;
  cardSet: string | null | undefined;
  gradingCompany: string | null | undefined;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["similar-auctions", currentId, cardSet, gradingCompany],
    queryFn: async () => {
      const q = supabase
        .from("auctions")
        .select(CARD_COLS)
        .eq("status", "active")
        .neq("id", currentId)
        .gt("end_time", new Date().toISOString())
        .limit(8);
      const filters: string[] = [];
      if (cardSet) filters.push(`card_set.ilike.%${cardSet}%`);
      if (gradingCompany) filters.push(`grading_company.eq.${gradingCompany}`);
      const { data } = filters.length
        ? await q.or(filters.join(","))
        : await q.order("end_time", { ascending: true });
      return (data ?? []) as (AuctionCardData & { card_set?: string })[];
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="mt-14">
        <h2 className="font-display text-2xl mb-4">Similar auctions</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <Reveal className="mt-14">
      <h2 className="font-display text-2xl mb-4">Similar auctions</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {data.slice(0, 4).map((a) => (
          <AuctionCard key={a.id} auction={a} />
        ))}
      </div>
    </Reveal>
  );
}

/** Other active auctions from the same seller, excluding current. */
export function SellerOtherAuctions({
  currentId,
  sellerId,
  sellerName,
}: {
  currentId: string;
  sellerId: string | null | undefined;
  sellerName?: string | null;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["seller-other-auctions", currentId, sellerId],
    enabled: !!sellerId,
    queryFn: async () => {
      const { data } = await supabase
        .from("auctions")
        .select(CARD_COLS)
        .eq("status", "active")
        .eq("seller_id", sellerId!)
        .neq("id", currentId)
        .gt("end_time", new Date().toISOString())
        .order("end_time", { ascending: true })
        .limit(8);
      return (data ?? []) as AuctionCardData[];
    },
    staleTime: 60_000,
  });

  if (!sellerId || isLoading || !data || data.length === 0) return null;

  return (
    <Reveal className="mt-14">
      <div className="flex items-end justify-between gap-4 mb-4">
        <h2 className="font-display text-2xl">
          More from {sellerName || "this seller"}
        </h2>
        <Link
          to="/seller/$sellerId"
          params={{ sellerId }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          View all →
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {data.slice(0, 4).map((a) => (
          <AuctionCard key={a.id} auction={a} />
        ))}
      </div>
    </Reveal>
  );
}
